import React, { useEffect, useState } from 'react';

import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import Routes from './globals/routes';
import theme from './globals/theme';
import configureStore from './store';
import { LoaderProvider } from './globals/loader/loader-context';
import SnackbarNotifier from './components/snackbar-notifier';
import CustomSnackbarDownloadable from './components/snackbar-notifier/custom-snackbar-downloadable';
import CenteredMessageBox from './components/centered-message-box';
import AuthLayer from './globals/auth-layer';
import { checkEnvironment } from './utilities';
import { axiosInstance } from "./axios-instance";
import { loadRuntimeConfig, getFedLoginUrl } from './config/runtime-config';
import { safeParseJwtPayload } from './globals/authentication/jwt';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme { }
}

const sleep = (seconds: number) => new Promise(resolve => setTimeout(resolve, 1000 * seconds));

const AUTH_TOKENS_UPDATED_EVENT = 'auth:tokens-updated';
const REFRESH_BUFFER_MS = 2 * 60 * 1000;
const REFRESH_JITTER_MS = 15 * 1000;
const MIN_REFRESH_RETRY_MS = 10 * 1000;

const TOKEN_ENDPOINT_PATH = '/as/token.oauth2';

const isTokenEndpointRequest = (url?: string) => {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.endsWith(TOKEN_ENDPOINT_PATH);
  } catch {
    return /\/as\/token\.oauth2(?:$|\?)/.test(url);
  }
};

let refreshTimeoutId: number | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let hasForcedReauthReload = false;

const isInvalidGrantRefreshError = (error: any): boolean => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const errorCode = data?.error;
  const description = String(data?.error_description ?? '').toLowerCase();

  if (status !== 400) {
    return false;
  }

  if (errorCode === 'invalid_grant') {
    return true;
  }
  return description.includes('expired refresh token') || description.includes('invalid refresh token');
};

const getJwtExpMs = (token?: string | null): number | null => {
  if (!token) {
    return null;
  }
  const payload = safeParseJwtPayload(token);
  const expSeconds = payload?.exp;
  if (typeof expSeconds !== 'number') {
    return null;
  }
  return expSeconds * 1000;
};

const clearProactiveRefreshTimer = () => {
  if (refreshTimeoutId !== null) {
    window.clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
};

const forceReauthReload = () => {
  if (hasForcedReauthReload) {
    return;
  }
  hasForcedReauthReload = true;

  clearProactiveRefreshTimer();
  refreshInFlight = null;

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  // eslint-disable-next-line no-console
  console.warn('Refresh token invalid/expired. Reloading to re-authenticate.');
  window.location.reload();
};

async function getNewAccessToken() {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return null;
    }
    const fedLoginURL = getFedLoginUrl();
    const tokenUrl = `${fedLoginURL}${TOKEN_ENDPOINT_PATH}`;

    // Security: keep refresh_token out of the URL (it can leak via logs/proxies/history).
    const parameters = new URLSearchParams();
    parameters.append('grant_type', 'refresh_token');
    parameters.append('refresh_token', refreshToken);

    const response = await axiosInstance.post(tokenUrl, parameters, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    const { access_token, refresh_token } = response.data;
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    }
    if (access_token) {
      localStorage.setItem('accessToken', access_token);
      window.dispatchEvent(new Event(AUTH_TOKENS_UPDATED_EVENT));
    }
    return access_token;
  } catch (error) {
    if (isInvalidGrantRefreshError(error)) {
      forceReauthReload();
      return null;
    }
    // eslint-disable-next-line no-console
    console.error('Error getting new access token:', error);
    return null;
  }
}

const scheduleProactiveRefresh = () => {
  clearProactiveRefreshTimer();

  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  if (!accessToken || !refreshToken) {
    return;
  }

  const expMs = getJwtExpMs(accessToken);
  if (!expMs) {
    return;
  }

  const now = Date.now();
  const jitterMs = Math.floor(Math.random() * REFRESH_JITTER_MS);
  const delayMs = Math.max(0, expMs - now - REFRESH_BUFFER_MS - jitterMs);

  refreshTimeoutId = window.setTimeout(async () => {
    try {
      if (!refreshInFlight) {
        refreshInFlight = (async () => {
          try {
            return await getNewAccessToken();
          } finally {
            refreshInFlight = null;
          }
        })();
      }

      const refreshedToken = await refreshInFlight;
      if (refreshedToken) {
        scheduleProactiveRefresh();
      } else {
        refreshTimeoutId = window.setTimeout(() => scheduleProactiveRefresh(), MIN_REFRESH_RETRY_MS);
      }
    } catch (refreshError) {
      // eslint-disable-next-line no-console
      console.error('Proactive token refresh failed:', refreshError);
      refreshTimeoutId = window.setTimeout(() => scheduleProactiveRefresh(), MIN_REFRESH_RETRY_MS);
    }
  }, delayMs);
};

axiosInstance.interceptors.request.use(async (config: any) => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken && !isTokenEndpointRequest(config?.url)) {
    return {
      ...config,
      headers: {
        ...(config?.headers ?? {}),
        Authorization: `Bearer ${accessToken}`,
      },
    };
  }
  return config;
});

axiosInstance.interceptors.response.use((response) => (response), async (error: any) => {
  const originalRequest = error.config;
  if (
    error?.response?.status === 401 &&
    originalRequest &&
    !originalRequest.retry &&
    !isTokenEndpointRequest(originalRequest?.url)
  ) {
    const newAccessToken = await getNewAccessToken();
    if (newAccessToken) {
      originalRequest.retry = true;
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      await sleep(1);
      return axiosInstance(originalRequest);
    }
  }
  // eslint-disable-next-line no-console
  console.error(error);
  return Promise.reject(error);
});

const store = configureStore();
const environment = checkEnvironment();
const App = () => {
  const [isClickJacked, setIsClickJacked] = useState(false);

  useEffect(() => {
    document.title = `Telematics Gateway - ${environment?.toUpperCase()}`;
    const checkclickJacking = () => {
      if (window.top && window.top !== window.self) {
        setIsClickJacked(true);
      }
    };
    checkclickJacking();

    const loadConfig = async () => {
      try {
        await loadRuntimeConfig(); // Load static.json in background
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load runtime configuration:', error);
      }
    };

    loadConfig();

    const handleTokensUpdated = () => scheduleProactiveRefresh();
    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, handleTokensUpdated);
    scheduleProactiveRefresh();

    return () => {
      window.removeEventListener(AUTH_TOKENS_UPDATED_EVENT, handleTokensUpdated);
      clearProactiveRefreshTimer();
    };
  }, []);


  if (isClickJacked) {
    return <CenteredMessageBox message="Page can't accessable" />;
  }

  return (
    <Provider store={store}>
      <LoaderProvider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <AuthLayer>
              <CssBaseline>
                <SnackbarProvider
                  maxSnack={3}
                  content={(key: string | number, message: string) => (
                    <CustomSnackbarDownloadable id={key} message={message} />
                  )}
                >
                  <Routes />
                  <SnackbarNotifier />
                </SnackbarProvider>
              </CssBaseline>
            </AuthLayer>
          </ThemeProvider>
        </StyledEngineProvider>
      </LoaderProvider>
    </Provider>
  )
}

export default App;
